import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Person, SavedView } from '../api/types'
import { AddContactModal } from '../components/AddContactModal'
import { Icon } from '../components/Icon'
import { ImportModal } from '../components/ImportModal'
import { Empty, ErrorState, Loading } from '../components/States'
import { StatusBoard } from '../components/StatusBoard'
import { useToast } from '../components/Toast'
import { initials, relativeDays, roleList, titleCase, usd } from '../lib/format'
import { tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

// The chips are the branch's own role vocabulary (crm_contact_role), not a list
// baked in here — a role added under Customize appears without a deploy. Only
// roles somebody actually holds get a chip; an empty one is noise.
//
// `contact_role` is what the contact record says someone is. It is a different
// question from `role`, which is the part they play on a loan file, and the two
// filters are kept apart on purpose.
// The two books. `leads` is what the Lead Platform's campaigns fill; `mine` is
// everyone an officer added, imported, or met on a loan file. The split is
// contact.source, which has recorded it since before the CRM existed — the
// separation the demo asked for needed a tab, not a data model.
const BOOKS = [
  { key: 'mine' as const, label: 'My contacts' },
  { key: 'leads' as const, label: 'Leads' },
]

const EVERYONE = { key: null as string | null, label: 'Everyone' }
const UNSET = { key: '', label: 'No role set' }

const GRID = '29px 1.6fr 1.05fr 0.6fr 0.75fr 0.75fr 0.8fr'
const PAGE = 100

/** Where a directory row opens: the contact card when they're in the lead book,
 *  otherwise the person view built from their loan files. */
export function hrefForPerson(p: Pick<Person, 'contact_id' | 'person_key'>): string {
  return p.contact_id ? `/contacts/c/${p.contact_id}` : `/partners/${encodeURIComponent(p.person_key)}`
}

export function Contacts() {
  const [q, setQ] = useState('')
  const [filtering, setFiltering] = useState(false)
  const [debounced, setDebounced] = useState('')
  const [role, setRole] = useState('')
  const [contactRole, setContactRole] = useState<string | null>(null)
  const [book, setBook] = useState<'leads' | 'mine'>('mine')
  // The status board is about the officer's own contacts, so it is a second view
  // of the My contacts tab rather than a page of its own. Campaign leads have no
  // place on it — they are worked from the Leads queue until someone takes one on.
  const [view, setView] = useState<'table' | 'board'>('table')
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [views, setViews] = useState<SavedView[]>([])
  const [naming, setNaming] = useState(false)
  const [viewName, setViewName] = useState('')
  const nav = useNavigate()
  const toast = useToast()
  const { data: roleData } = useAsync(() => api.contactRoles(), [])

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 220)
    return () => window.clearTimeout(t)
  }, [q])
  useEffect(() => setPage(0), [debounced, role, contactRole, book])

  // Arrived from the New contacts goal: the same people the number counted, which
  // is the question an officer is actually asking when they click it.
  const added = params.get('added')
  const addedWindow = useMemo(() => {
    if (!added) return null
    const [y, m] = added.split('-').map(Number)
    if (!y || !m) return null
    const from = new Date(y, m - 1, 1)
    return { from: from.toISOString(), to: new Date(y, m, 1).toISOString(),
             label: from.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) }
  }, [added])

  const { data, error, loading, reload } = useAsync(
    () => api.people({ q: debounced || undefined, role: role || undefined,
                       contact_role: contactRole ?? undefined, book,
                       added_by_me: addedWindow ? true : undefined,
                       added_from: addedWindow?.from, added_to: addedWindow?.to }),
    [debounced, role, contactRole, book, added],
  )
  useEffect(() => {
    api.prefs().then((p) => setViews(p.views ?? [])).catch(() => {})
  }, [])

  const people = data?.people ?? []
  const pages = Math.max(1, Math.ceil(people.length / PAGE))
  const rows = people.slice(page * PAGE, (page + 1) * PAGE)

  async function saveViews(next: SavedView[]) {
    try {
      await api.setPref('views', next)
      setViews(next)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  async function saveCurrent() {
    const name = viewName.trim()
    if (!name) return setNaming(false)
    await saveViews([...views.filter((v) => v.name !== name), { name, role, q: debounced }])
    setViewName('')
    setNaming(false)
    toast(`Saved view “${name}”`, { tone: 'positive' })
  }

  const activeView = views.find((v) => v.role === role && v.q === debounced)

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="h1">Contacts</h1>
          <p className="subline">
            Everyone you know — your own people and everyone on a loan file — one record each.
            Campaign leads are worked from <Link to="/leads">Leads</Link>.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {book === 'mine' ? (
            <div className="seg" style={{ marginRight: 2 }}>
              <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}
                title="List" aria-label="List view"><Icon name="table_rows" size={17} /></button>
              <button className={view === 'board' ? 'active' : ''} onClick={() => setView('board')}
                title="Status board" aria-label="Status board"><Icon name="view_kanban" size={17} /></button>
            </div>
          ) : null}
          <a className="btn" href={api.exportContactsUrl('xlsx')} download
            title="Download your contacts as an Excel workbook">
            <Icon name="download" size={17} /> Export
          </a>
          <button className="btn" onClick={() => setImporting(true)}>
            <Icon name="upload_file" size={17} /> Import
          </button>
          <button className="btn primary" onClick={() => setAdding(true)}>
            <Icon name="person_add" size={17} /> Add contact
          </button>
        </div>
      </div>

      {/* Which book, then which kind within it. Two questions, so two rows —
          folding the leads into the role chips would have hidden 744 people
          behind a filter nobody would think to clear. */}
      <div className="tabs" style={{ marginBottom: 10 }}>
        {BOOKS.map((b) => (
          <button key={b.key} role="tab" aria-selected={book === b.key}
            className={`tab${book === b.key ? ' active' : ''}`}
            onClick={() => { setBook(b.key); setContactRole(null); setQ(''); if (b.key !== 'mine') setView('table') }}>
            {b.label}
            {data?.books?.[b.key] ? <span className="count" style={{ marginLeft: 6 }}>{data.books[b.key]}</span> : null}
          </button>
        ))}
      </div>

      {view === 'board' ? <StatusBoard /> : (
      <>
      <div className="smartlist">
        {[{ ...EVERYONE, n: 0 },
          // Counts come from the list response, so they are scoped to the book
          // on screen rather than the whole database.
          ...(roleData?.roles ?? [])
            .map((r) => ({ key: r.key as string | null, label: r.label, n: data?.role_counts?.[r.key] ?? 0 }))
            .filter((r) => r.n),
          ...(data?.role_counts?.[''] ? [{ ...UNSET, n: data.role_counts[''] }] : [])].map((l) => (
          <button
            key={l.key ?? 'all'}
            className={`slchip${contactRole === l.key && !activeView ? ' active' : ''}`}
            onClick={() => { setContactRole(l.key); setRole(''); setQ('') }}
          >
            {l.label}
            {l.n ? <span className="count" style={{ marginLeft: 5 }}>{l.n}</span> : null}
          </button>
        ))}
        {views.map((v) => (
          <span key={v.name} className={`slchip${activeView?.name === v.name ? ' active' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, paddingRight: 6 }}>
            <button style={{ border: 0, background: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}
              onClick={() => { setRole(v.role); setQ(v.q) }}>
              <Icon name="bookmark" size={13} style={{ verticalAlign: -2 }} /> {v.name}
            </button>
            <button style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink-3)', display: 'flex' }}
              aria-label={`Delete view ${v.name}`} onClick={() => saveViews(views.filter((x) => x.name !== v.name))}>
              <Icon name="close" size={13} />
            </button>
          </span>
        ))}
        {naming ? (
          <input
            className="tag-input"
            autoFocus
            placeholder="Name this view"
            value={viewName}
            maxLength={40}
            onChange={(e) => setViewName(e.target.value)}
            onBlur={saveCurrent}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveCurrent()
              if (e.key === 'Escape') { setViewName(''); setNaming(false) }
            }}
          />
        ) : (
          <button className="slchip dashed" onClick={() => setNaming(true)} disabled={!role && !debounced}
            title={!role && !debounced ? 'Pick a list or search first' : 'Save this list and search as a view'}>
            ＋ Save current filters
          </button>
        )}
      </div>

      <div className="card">
        <div className="gt-toolbar">
          {/* No second search box on a screen that already has one in the header.
              The filter is an icon until it is wanted, and stays open while it
              holds anything — so the capability is here without the duplication. */}
          {filtering || q ? (
            <span className="gt-filter">
              <Icon name="filter_alt" size={16} />
              <input
                className="field"
                autoFocus
                placeholder="Filter these rows"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Filter these rows"
              />
              <button className="iconbtn" aria-label="Close the filter"
                onClick={() => { setQ(''); setFiltering(false) }}>
                <Icon name="close" size={15} />
              </button>
            </span>
          ) : (
            <button className="iconbtn" aria-label="Filter these rows" title="Filter these rows"
              onClick={() => setFiltering(true)}>
              <Icon name="filter_alt" size={17} />
            </button>
          )}
          {addedWindow ? (
            <span className="badge t-accent">
              Added by you in {addedWindow.label}
              <button className="linklike" style={{ marginLeft: 6 }}
                onClick={() => { params.delete('added'); setParams(params, { replace: true }) }}>
                clear
              </button>
            </span>
          ) : null}
          {contactRole !== null ? (
            <span className="badge t-accent">
              {contactRole === '' ? UNSET.label
                : roleData?.roles.find((r) => r.key === contactRole)?.label ?? titleCase(contactRole)}
            </span>
          ) : null}
          {role ? (
            <span className="badge t-accent">{titleCase(role)}</span>
          ) : null}
          <span className="spacer" />
          <span className="quiet" style={{ fontSize: 12 }}>{people.length} people</span>
        </div>

        {loading && !data ? (
          <Loading />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : people.length === 0 ? (
          <Empty title="Nobody matches">
            Try a different search, or <button className="btn sm" onClick={() => setAdding(true)}>add a contact</button>
          </Empty>
        ) : (
          <>
            <div className="gt-head" style={{ gridTemplateColumns: GRID }}>
              <span />
              <span>Name</span>
              <span>Roles</span>
              <span className="right">Loans</span>
              <span className="right">Volume</span>
              <span>Lead stage</span>
              <span>Last touch</span>
            </div>
            {rows.map((p) => (
              <button
                className="gt-row"
                key={p.person_key}
                style={{ gridTemplateColumns: GRID }}
                onClick={() => nav(hrefForPerson(p))}
              >
                <span className="gt-av" style={tint(p.name)}>{initials(p.name)}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="gt-name">{p.name ?? '—'}</span>
                  {/* Who they are, not how to reach them: the row is for finding the
                      person, and every way of contacting them is one click away. */}
                  <span className="gt-sub">{[p.company, p.email].filter(Boolean).join(' · ') || '—'}</span>
                </span>
                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {roleList(p.roles).length ? (
                    roleList(p.roles).map((r) => (
                      <span className="badge t-neutral" key={r}>{titleCase(r)}</span>
                    ))
                  ) : p.in_lead_book ? (
                    <span className="badge t-neutral">Lead</span>
                  ) : (
                    <span style={{ color: 'var(--ink-4)' }}>—</span>
                  )}
                </span>
                <span className="right mono">{p.loans || '—'}</span>
                <span className="right mono">{p.volume ? usd(p.volume) : '—'}</span>
                <span>
                  {p.in_lead_book ? (
                    <span className="badge t-accent">{titleCase(p.lead_status)}</span>
                  ) : (
                    <span style={{ color: 'var(--ink-4)' }}>—</span>
                  )}
                </span>
                <span className="gt-sub">{relativeDays(p.last_activity)}</span>
              </button>
            ))}
            <div className="gt-foot">
              <span>
                {page * PAGE + 1}–{Math.min((page + 1) * PAGE, people.length)} of {people.length}
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button className="iconbtn" disabled={page === 0} onClick={() => setPage((n) => n - 1)} aria-label="Previous page">
                  <Icon name="chevron_left" size={17} />
                </button>
                <button className="iconbtn" disabled={page >= pages - 1} onClick={() => setPage((n) => n + 1)} aria-label="Next page">
                  <Icon name="chevron_right" size={17} />
                </button>
              </span>
            </div>
          </>
        )}
      </div>

      </>
      )}

      <AddContactModal open={adding} onClose={() => setAdding(false)} />
      <ImportModal open={importing} onClose={() => setImporting(false)} onDone={reload} />
    </div>
  )
}
